// Labels for the live chat states and outcomes stored by the server (app/services/live_chat.py)

const STATUS_LABELS = {
  requested: "Waiting",
  active: "Live",
  claimed: "Live",
  resolved: "Resolved",
  auto_resolved: "Auto-resolved",
  timed_out: "No agent in time",
  unavailable: "No agent online",
  closed: "Closed",
};

const OUTCOME_LABELS = {
  resolved: "Resolved by agent",
  auto_resolved: "Auto-resolved (idle)",
  wait_timeout: "No agent accepted in time",
  no_agent_online: "No agent online",
  visitor_ended: "Ended by visitor",
};

export const liveChatStatusLabel = (status) => STATUS_LABELS[status] || "";

export const liveChatOutcomeLabel = (reason) => OUTCOME_LABELS[reason] || reason || "";

export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return "";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

// ── chat list / transcript helpers (live chat screen) ──────────────────────────

// Seconds or milliseconds or ISO string -> milliseconds (0 when unknown)
export const toMs = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value < 10000000000 ? value * 1000 : value;
  if (/^\d+(\.\d+)?$/.test(String(value))) return toMs(Number(value));
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
};

export const shortId = (id) => (id ? String(id).slice(-6).toUpperCase() : "");

// One name for a visitor everywhere on the screen
export const visitorName = (session) =>
  (session && (session.user_name || session.user_email || session.user_phone)) ||
  `Visitor ${shortId(session && session.session_id)}`;

export const channelOf = (session) => {
  const ch = String((session && session.channel) || "").toLowerCase();
  const sid = String((session && session.session_id) || "").toLowerCase();
  if (ch === "whatsapp" || /^\d{7,}$/.test(sid)) return "whatsapp";
  if (ch === "telegram" || sid.startsWith("telegram_")) return "telegram";
  if (ch === "slack" || sid.startsWith("slack_")) return "slack";
  if (ch === "email" || sid.startsWith("email_")) return "email";
  if (ch === "freshchat" || sid.startsWith("freshchat_")) return "freshchat";
  return "webchat";
};

export const CHANNEL_LABELS = {
  webchat: "Website",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  slack: "Slack",
  email: "Email",
  freshchat: "Freshchat",
};

// "now", "4m", "2h", "3d"
export const shortAgo = (ms, now = Date.now()) => {
  if (!ms) return "";
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 45) return "now";
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

export const isWaiting = (session) => !!session && session.handoff_status === "requested";
export const isLive = (session) => !!session && (session.handoff_status === "active" || session.handoff_status === "claimed");

// "Today" / "Yesterday" / "Mon, 20 Oct"
export const dayLabel = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

// "/uploads/..." paths of chat images and files are served by the API (/api/uploads/...)
export const mediaUrl = (path) => {
  const p = String(path || "");
  if (/^(https?:|data:)/.test(p)) return p;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  return p.startsWith("/uploads") ? `${apiBase}${p}` : p;
};

// Images in a message are "::IMAGE::<path>" lines (visitor and agent uploads, knowledge base pictures)
export const splitImages = (text) => {
  const images = [];
  const body = String(text || "").replace(/::IMAGE::(\S+)/g, (_, url) => {
    images.push(url);
    return "";
  });
  return { body: body.replace(/\n{3,}/g, "\n\n").trim(), images };
};

// A history entry as text: the message plus its image / file (an image-only message has no text)
export const historyText = (msg) => {
  const parts = [String(msg.message || "").trim()];
  const image = msg.image && String(msg.image);
  if (image && !parts[0].includes(image)) {
    parts.push(`::IMAGE::${/^(https?:|data:|\/)/.test(image) ? image : `/uploads/images/${image}`}`);
  }
  if (msg.file && !parts[0].includes(String(msg.file))) parts.push(`[File: ${msg.file}]`);
  return parts.filter(Boolean).join("\n\n");
};

// Bot answers list their options as "[Label]" lines: split them off so they can be shown as chips
export const splitQuickReplies = (text) => {
  const options = [];
  const lines = String(text || "").split("\n");
  const kept = lines.filter((line) => {
    const m = line.match(/^\s*(?:[-*]\s*|\d+\.\s*)?\[([^\]\n]{1,80})\](?!\()\s*$/);
    if (m) {
      options.push(m[1].trim());
      return false;
    }
    return true;
  });
  return { body: kept.join("\n").trim(), options };
};
